import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Input, List, Modal, Spin, Tag, message } from 'antd';
import { CheckCircleOutlined, UndoOutlined, SendOutlined, BellOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Velinin "bilgilerimi güncelleyin" istekleri.
 *
 * "Veli Talepleri" (devamsızlık/telafi) ekranıyla KARIŞTIRMA — ayrı akış,
 * ayrı izin. O liste öğretmene de açık; bu istek metni velinin e-posta/
 * telefon bilgisini taşıdığı için yalnızca yöneticide.
 *
 * Yönetici buraya bildirime tıklayarak geliyor: metni okuyor, değişikliği
 * Profil Bilgileri sekmesinden yapıyor, sonra burada "Yapıldı" işaretliyor.
 * Onay bir iş akışı başlatmıyor — bu bir İŞ KUYRUĞU, unutulmasın diye.
 */
export default function ParentProfileChangeRequestsTab({ userId }) {
  const [istekler, setIstekler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kaydeden, setKaydeden] = useState(null);
  const [notModal, setNotModal] = useState(null);   // { id }
  const [not, setNot] = useState('');

  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/parent-profile-change-requests', {
        params: { parent_user_id: userId, per_page: 50 },
      });
      setIstekler(res.data.data || []);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İstekler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { yukle(); }, [yukle]);

  const isaretle = async (id, govde = {}) => {
    setKaydeden(id);
    try {
      const res = await api.patch(`/parent-profile-change-requests/${id}/toggle-done`, govde);
      message.success(res.data.message);
      setNotModal(null);
      setNot('');
      yukle();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setKaydeden(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Spin /></div>;

  if (istekler.length === 0) {
    return <Empty description="Bu veliden bilgi güncelleme isteği yok." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <>
      <List
        dataSource={istekler}
        rowKey="id"
        renderItem={(item) => {
          const yapildi = item.status === 'done';
          return (
            <List.Item
              key={item.id}
              actions={[
                yapildi ? (
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    loading={kaydeden === item.id}
                    onClick={() => isaretle(item.id)}
                  >
                    Geri Al
                  </Button>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={kaydeden === item.id}
                    onClick={() => setNotModal({ id: item.id })}
                  >
                    Yapıldı
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                title={
                  <div className="flex items-center gap-2 flex-wrap">
                    {yapildi
                      ? <Tag color="green">Yapıldı</Tag>
                      : <Tag color="orange">Bekliyor</Tag>}
                    <span className="text-gray-400 text-sm">
                      {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </div>
                }
                description={
                  <div>
                    {/* Velinin yazdığı düz metin. HTML'e çevrilmiyor —
                        sanitize edilse bile gereksiz saldırı yüzeyi. */}
                    <div className="whitespace-pre-wrap text-gray-800">{item.message}</div>
                    {yapildi && (
                      <div className="text-xs text-gray-400 mt-2">
                        {item.done_by?.name
                          ? `${item.done_by.name} ${item.done_by.surname}`
                          : 'Bilinmiyor'}
                        {item.done_at ? ` — ${dayjs(item.done_at).format('DD.MM.YYYY HH:mm')}` : ''}
                        {item.done_note ? ` · ${item.done_note}` : ''}
                        {/* "Haber verdik mi" sorusunun cevabı: not yazılmış
                            olması bildirim gittiği anlamına gelmiyor. */}
                        {item.notified_at
                          ? <Tag color="blue" className="ml-2">Veliye bildirildi</Tag>
                          : <Tag className="ml-2">Bildirim gönderilmedi</Tag>}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />

      {/*
        ÜÇ KAPATMA YOLU (kullanıcının isteği, 2026-09-17). Üçüncüsü şart:
        gelen isteklerin bir kısmına ne yazılacağı belli olmuyor, yönetici
        okuyup sessizce kapatabilmeli. Zorunlu bildirim olsaydı ya anlamsız
        mesajlar giderdi ya da istekler açık kalırdı.
      */}
      <Modal
        title="İsteği kapat"
        open={!!notModal}
        onCancel={() => { setNotModal(null); setNot(''); }}
        footer={null}
        width={520}
      >
        <p className="text-gray-500 mb-3">
          Veliye ne bildirileceğini seçin. Kapatma her durumda kaydediliyor.
        </p>

        <Input.TextArea
          rows={3}
          value={not}
          onChange={(e) => setNot(e.target.value)}
          maxLength={1000}
          placeholder="Veliye iletilecek mesaj (örn: E-posta adresiniz güncellendi.)"
        />

        <div className="flex flex-col gap-2 mt-4">
          <Button
            type="primary"
            icon={<SendOutlined />}
            block
            disabled={!not.trim()}
            loading={kaydeden === notModal?.id}
            onClick={() => isaretle(notModal.id, { mode: 'reply', done_note: not.trim() })}
          >
            Mesajı gönder ve kapat
          </Button>
          <Button
            block
            icon={<BellOutlined />}
            loading={kaydeden === notModal?.id}
            onClick={() => isaretle(notModal.id, { mode: 'notify', done_note: not.trim() || null })}
          >
            Sadece "tamamlandı" bildirimi gönder
          </Button>
          <Button
            block
            icon={<StopOutlined />}
            loading={kaydeden === notModal?.id}
            onClick={() => isaretle(notModal.id, { mode: 'silent', done_note: not.trim() || null })}
          >
            Bildirim göndermeden kapat
          </Button>
        </div>

        <p className="text-gray-400 text-xs mt-3">
          İlk seçenekte yazdığınız metin veliye olduğu gibi iletilir. Diğer
          iki seçenekte yazdığınız metin yalnızca burada kayıtlı kalır.
        </p>
      </Modal>
    </>
  );
}
